/**
 * PATH: apps/qa/src/pages/template-detail/SortableItem.tsx
 * PURPOSE: Draggable template checklist item with inline editing
 */

import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useState } from 'react'
import { ChevronDown, ChevronRight, GripVertical, Trash2 } from 'lucide-react'
import type { TemplateItem } from './types'
import { getItemId } from './types'

interface SortableItemProps {
  item: TemplateItem
  itemIndex: number
  sectionIndex: number
  onUpdate: (updates: Partial<TemplateItem>) => void
  onRemove: () => void
}

export function SortableItem({ item, itemIndex, sectionIndex, onUpdate, onRemove }: SortableItemProps) {
  const [isOpen, setIsOpen] = useState(false)
  const id = getItemId(item, sectionIndex, itemIndex)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }

  const updateRule = (key: string, value: unknown) =>
    onUpdate({ evidence_rules: { ...item.evidence_rules, [key]: value } })

  return (
    <div ref={setNodeRef} style={style} className="rounded-lg bg-muted/50 border border-border">
      <div className="flex items-center gap-2 p-3">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing touch-none" aria-label="Reorder item">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        <span className="text-xs font-mono text-muted-foreground">{item.item_number}</span>
        <input type="text" value={item.title} onChange={(e) => onUpdate({ title: e.target.value })}
          aria-label={`Item ${item.item_number} title`}
          className="min-w-0 flex-1 bg-transparent text-foreground text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/20 px-2 py-1 rounded" />
        {item.has_hold_point && <span className="hidden sm:inline text-xs rounded-full bg-amber-100 px-2 py-0.5 text-amber-700">Hold point</span>}
        <button type="button" onClick={() => setIsOpen((value) => !value)} aria-expanded={isOpen}
          className="p-1.5 rounded hover:bg-accent text-muted-foreground" aria-label={`${isOpen ? 'Close' : 'Open'} item details`}>
          {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
        <button type="button" onClick={onRemove} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" aria-label="Remove item">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {isOpen && (
        <div className="border-t border-border bg-background/70 p-4 grid gap-4 lg:grid-cols-2">
          <Field label="Requirement">
            <textarea value={item.requirement_text} onChange={(e) => onUpdate({ requirement_text: e.target.value })} rows={4} className={fieldClass} />
          </Field>
          <Field label="Acceptance criteria">
            <textarea value={item.acceptance_criteria} onChange={(e) => onUpdate({ acceptance_criteria: e.target.value })} rows={4} className={fieldClass} />
          </Field>
          <Field label="Product / system">
            <input value={item.product || ''} onChange={(e) => onUpdate({ product: e.target.value })} className={fieldClass} />
          </Field>
          <Field label="Responsible role">
            <input value={item.responsible_role} onChange={(e) => onUpdate({ responsible_role: e.target.value })} className={fieldClass} />
          </Field>
          <Field label="Evidence instructions">
            <textarea value={String(item.evidence_rules.instructions || '')} onChange={(e) => updateRule('instructions', e.target.value)} rows={2} className={fieldClass} />
          </Field>
          <Field label="Standard references / citations">
            <textarea value={String(item.evidence_rules.citations || '')} onChange={(e) => updateRule('citations', e.target.value)} rows={2} className={fieldClass} />
          </Field>
          <div className="flex flex-wrap items-center gap-5 lg:col-span-2">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={item.has_hold_point} onChange={(e) => onUpdate({ has_hold_point: e.target.checked })} />Hold point</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(item.evidence_rules.requires_photo)} onChange={(e) => updateRule('requires_photo', e.target.checked)} />Photo required</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(item.evidence_rules.requires_reading)} onChange={(e) => updateRule('requires_reading', e.target.checked)} />Reading required</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={item.is_active} onChange={(e) => onUpdate({ is_active: e.target.checked })} />Active</label>
          </div>
          {item.has_hold_point && <Field label="Hold point release instruction"><textarea value={String(item.hold_point_config.instruction || '')} onChange={(e) => onUpdate({ hold_point_config: { ...item.hold_point_config, instruction: e.target.value } })} rows={2} className={fieldClass} /></Field>}
        </div>
      )}
    </div>
  )
}

const fieldClass = 'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20'

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block text-sm font-medium text-foreground"><span className="mb-1 block">{label}</span>{children}</label>
}
